import { useTranslation } from 'react-i18next'
import { AlertCircle } from 'lucide-react'
import { type UploadedImage } from '@/hooks'
import { Button, Modal } from '@/components/ui'
import type { TrolleySheetData } from '@/utils/tool-management/tool-management.util'
import { useToolImportModal } from '@/hooks/useToolManagement/useToolImportModal'

import { FileUploadArea } from './FileUploadArea'
import { ImageUploadArea } from './ImageUploadArea'
import { UploadedFileDisplay } from './UploadedFileDisplay'
import { UploadedImagesList } from './UploadedImagesList'

interface ToolImportModalProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onImportComplete?: (data: {
    file: Nullable<File>
    images: UploadedImage[]
    data: Nullable<TrolleySheetData>
  }) => void
}

export function ToolImportModal({
  isOpen,
  onOpenChange,
  onImportComplete,
}: ToolImportModalProps) {
  const { t } = useTranslation()
  const {
    fileInputRef,
    imageInputRef,
    uploadedImages,
    imageError,
    uploadedFile,
    uploadProgress,
    isUploading,
    error,
    toolManagementData,
    isDragging,
    isDraggingImage,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    triggerFileInput,
    triggerImageInput,
    handleImageChange,
    handleImageDragOver,
    handleImageDragLeave,
    handleImageDrop,
    removeImage,
    handleCloseModal,
    handleRemoveFile,
    handleRetryProcessing,
  } = useToolImportModal()

  const handleModalClose = () => {
    handleCloseModal()
    onOpenChange(false)
  }

  const handleImport = () => {
    if (!toolManagementData) {
      return
    }

    if (onImportComplete) {
      onImportComplete({
        file: uploadedFile,
        images: uploadedImages,
        data: toolManagementData,
      })
    }
    handleModalClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={handleModalClose}
      title={t('toolManagement.import')}
      description={t('toolManagement.importDescription')}
      className='max-h-[85vh] min-h-[80vh] flex flex-col'
    >
      <div className='flex-1 flex flex-col overflow-y-auto'>
        <div className='flex-1 flex flex-col space-y-6'>
          <div className='space-y-2 flex-shrink-0'>
            <label className='text-sm font-medium text-gray-700'>
              {t('toolManagement.importFile')}
            </label>
            {!uploadedFile && (
              <FileUploadArea
                isDragging={isDragging}
                onClick={triggerFileInput}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                dragText={t('toolManagement.dropFileHere')}
                clickText={t('toolManagement.clickToUploadText')}
                orDragAndDropText={t('toolManagement.orDragAndDrop')}
                formatInfo={t('toolManagement.fileFormatInfo')}
              />
            )}
            {uploadedFile && (
              <UploadedFileDisplay
                file={uploadedFile}
                uploadProgress={uploadProgress}
                onRemove={handleRemoveFile}
                fileSizeUnit={t('toolManagement.fileSizeUnitKB')}
                error={error}
                onRetry={handleRetryProcessing}
              />
            )}
          </div>

          <div className='flex-1 flex flex-col space-y-2 min-h-0'>
            <label className='text-sm font-medium text-gray-700 flex-shrink-0'>
              {t('toolManagement.importImages')}{' '}
              <span className='text-gray-500 font-normal'>
                ({t('common.optional')})
              </span>
            </label>
            <div className='flex-1 flex flex-col min-h-0'>
              <ImageUploadArea
                isDragging={isDraggingImage}
                onClick={triggerImageInput}
                onDragOver={handleImageDragOver}
                onDragLeave={handleImageDragLeave}
                onDrop={handleImageDrop}
                dragText={t('toolManagement.dropFileHere')}
                clickText={t('toolManagement.clickToUploadText')}
                orDragAndDropText={t('toolManagement.orDragAndDrop')}
                formatInfo={t('toolManagement.imageFormatInfo')}
              />
            </div>
            {imageError && (
              <div className='flex items-center space-x-2 p-3 bg-red-50 rounded-lg flex-shrink-0'>
                <AlertCircle className='h-5 w-5 text-red-600' />
                <p className='text-sm text-red-600'>{imageError}</p>
              </div>
            )}

            {uploadedImages.length > 0 && (
              <div className='flex-shrink-0'>
                <UploadedImagesList
                  images={uploadedImages}
                  onRemove={removeImage}
                  fileSizeUnit={t('toolManagement.fileSizeUnitKB')}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        id='file-upload'
        type='file'
        accept='.xls,.xlsx'
        onChange={handleFileChange}
        className='hidden'
        disabled={isUploading}
      />
      <input
        ref={imageInputRef}
        id='image-upload'
        type='file'
        accept='image/png,image/jpeg,image/jpg'
        multiple
        onChange={handleImageChange}
        className='hidden'
      />

      <div className='flex-shrink-0 flex justify-end gap-3 pt-4 mt-4 border-t'>
        <Button
          type='button'
          disabled={!uploadedFile || isUploading || !toolManagementData}
          onClick={handleImport}
        >
          {t('common.preview')}
        </Button>
      </div>
    </Modal>
  )
}
